/**
 * The daily phone check-in: about 30 seconds, once per shift. It enforces training without overhead:
 *   1. Six health yes/no questions (Food Code 2-201.11), with follow-ups after a sore or a diagnosis → the action (2-201.12)
 *   2. "I washed my hands just now" (Food Code 2-301.14)   → one tap
 *   3. One refresher card from the lessons                 → reaffirms, never replaces, the state course
 * Plus handwashing nudges during the shift, because handwashing can't be sensed (PRINCIPLES.md S6).
 */
import { illnessLine, shapesAt } from "./ruleshapes.ts";
import { LESSONS } from "./learn.ts";
import { CHECKIN_UT } from "./states/ut/checkin.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { cite, wordsFor } from "./states/words.ts";
import type { DailyCheckin, Id, IsoDate } from "./model.ts";

export const HEALTH_QUESTIONS: { key: keyof DailyCheckin["health"]; text: string }[] = [
  { key: "vomitDiarrhea", text: "Vomiting or diarrhea in the last 24 hours?" },
  { key: "jaundice", text: "Yellow skin or eyes (jaundice)?" },
  { key: "soreThroatFever", text: "Sore throat with a fever?" },
  { key: "diagnosedOrExposed", text: "Has a doctor diagnosed you with one of these illnesses: norovirus, hepatitis A, Shigella, E. coli (STEC), Salmonella or typhoid fever?" },
  { key: "uncoveredSore", text: "A cut, burn or sore with pus on your hands or arms that isn't covered?" },
  { key: "exposedAtHome", text: "Is someone you live with sick with vomiting or diarrhea, or diagnosed with one of those?" },
];
/** Asked only when uncoveredSore is yes. */
export const SORE_FOLLOW_UP = { key: "soreCovered" as const, text: "Cover it now with a bandage and wear a glove on that hand. Is it covered?" };

/**
 * After a yes to the diagnosis (fix/checkin-diagnosed-split, 2026-09-29; the owner's decision). The Food Code treats a
 * diagnosis with symptoms and one without differently, so one "diagnosed?" question can't give the right action:
 *   Food Code 2-201.12 (FDA Food Code 2022, adopted in Utah by R392-100), as the house table has it
 *   (docs/source/parish-food-safety-protocol.md, "Exclude or restrict"):
 *     (A)(2) "Symptomatic with vomiting or diarrhea and diagnosed with ... Norovirus, Shigella spp., Salmonella
 *            (nontyphoidal), or SHIGA TOXIN-PRODUCING E. COLI": EXCLUDE.            House: with symptoms | Exclude | Exclude
 *     (B)(2)-(3) hepatitis A, with symptoms or "without developing symptoms": EXCLUDE.  House: Exclude | Exclude
 *     (C)    typhoid fever: EXCLUDE.                                                  House: Exclude | Exclude
 *     (D)-(F) norovirus, Shigella, STEC and ASYMPTOMATIC: (1) exclude where a highly susceptible population is served;
 *            (2) restrict where not.                                                  House: Restrict | Exclude
 *     (G)    nontyphoidal Salmonella and ASYMPTOMATIC: RESTRICT, HSP or not.          House: Restrict | Restrict
 * Which illness matters only without symptoms, and only as far as it changes the action: in a regular place, is it
 * hepatitis A or typhoid (exclude) or one of the other four (restrict)? Where an HSP is served, is it nontyphoidal
 * Salmonella (restrict) or one of the other five (exclude)? So one of the two last questions is asked, never both.
 * "Symptoms" is the owner's list (the Food Code's reportable symptoms, 2-201.11(A)(1)(a)-(d)). A yes to the vomiting,
 * jaundice or sore-throat question already answers it, so it isn't asked again (fix S5: ask only what can change the action).
 */
export const DIAGNOSED_FOLLOW_UPS = {
  diagnosedSymptoms: { key: "diagnosedSymptoms" as const, text: "Do you have symptoms now (throwing up, diarrhea, yellow skin or eyes, or a sore throat with fever)?" },
  diagnosedHepATyphoid: { key: "diagnosedHepATyphoid" as const, text: "Did the doctor say hepatitis A or typhoid fever?" },
  diagnosedSalmonella: { key: "diagnosedSalmonella" as const, text: "Did the doctor say Salmonella (not typhoid fever)?" },
};
type Health = DailyCheckin["health"];
type FollowUpKey = keyof typeof DIAGNOSED_FOLLOW_UPS;
export type CheckinQuestionKey = keyof Health;
export interface CheckinQuestion { key: CheckinQuestionKey; text: string; followUp?: boolean }

/** A symptom is already known from the questions before the diagnosis, or from "symptoms now". */
function symptomKnown(h: Partial<Health>): boolean {
  return h.vomitDiarrhea === true || h.jaundice === true || h.soreThroatFever === true || h.diagnosedSymptoms === true;
}

/** The diagnosis follow-ups that apply to these answers (answered or not), in order. */
function diagnosedFollowUps(a: Partial<Health>): FollowUpKey[] {
  if (a.diagnosedOrExposed !== true) return [];
  const out: FollowUpKey[] = [];
  const known = a.vomitDiarrhea === true || a.jaundice === true || a.soreThroatFever === true;
  if (!known) out.push("diagnosedSymptoms");
  if (!known && a.diagnosedSymptoms === false) out.push(a.hsp ? "diagnosedSalmonella" : "diagnosedHepATyphoid");
  return out;
}

/**
 * The questions in the order they're asked: `base` (core's HEALTH_QUESTIONS, or the server's copy of them) with each
 * follow-up right after the answer it follows. `a.hsp` (the location serves a highly susceptible population) picks the
 * last diagnosis question. The web, the phone and the shop tablet all ask from this one list.
 */
export function checkinQuestionFlow(a: Partial<Health>, base: readonly { key: CheckinQuestionKey; text: string }[] = HEALTH_QUESTIONS): CheckinQuestion[] {
  const out: CheckinQuestion[] = [];
  for (const q of base) {
    out.push(q);
    if (q.key === "diagnosedOrExposed") for (const k of diagnosedFollowUps(a)) out.push(DIAGNOSED_FOLLOW_UPS[k]);
    if (q.key === "uncoveredSore" && a.uncoveredSore === true) out.push({ ...SORE_FOLLOW_UP, followUp: true });
  }
  return out;
}

/** Take back one answer, and the follow-ups that hang on it (Back on every check-in screen). */
export function withoutAnswer<T extends Partial<Health>>(a: T, key: CheckinQuestionKey): T {
  const n = { ...a };
  const drop: CheckinQuestionKey[] = [key];
  if (key === "uncoveredSore") drop.push("soreCovered");
  if (key === "diagnosedOrExposed") drop.push("diagnosedSymptoms", "diagnosedHepATyphoid", "diagnosedSalmonella");
  if (key === "diagnosedSymptoms") drop.push("diagnosedHepATyphoid", "diagnosedSalmonella");
  for (const k of drop) delete n[k];
  return n;
}

/** All refresher points, flattened, so staff cycle through every lesson point over time. */
export const REFRESHERS = LESSONS.flatMap((l) => l.points.map((text, i) => ({ lessonId: l.id, point: i, title: l.title, text })));

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  return h;
}

/** Deterministic per person per day, so the phone and server agree on today's card. */
export function refresherFor(employeeId: Id, date: IsoDate) {
  const day = Math.floor(Date.parse(date + "T00:00:00Z") / 86400000);
  return REFRESHERS[(hash(employeeId) + day) % REFRESHERS.length]!;
}

/**
 * What the Food Code says to do (2-201.12 exclude/restrict, 2-201.13 return, 2-401.13 lesions), per the source table.
 * The person in charge receives this action, never the symptom: minimum necessary disclosure (FRAMEWORK T4, refined).
 * The worker lines never say "your manager knows": whether anyone has it yet depends on the send (returntowork.ts
 * checkinToldLine), and the return rule (2-201.13) is returntowork.ts RETURN_RULES.
 * Ordered by severity; the most severe answer wins.
 */
export type CheckinAction = "ok" | "caution" | "cover" | "restrict" | "restrict_county" | "exclude" | "exclude_county";
/**
 * restrict_county sits above restrict (it adds the health department's OK to come back) and below exclude. It never
 * meets exclude in one check-in: exclude comes only from vomiting or diarrhea, which makes a diagnosis symptomatic.
 */
const SEVERITY: CheckinAction[] = ["ok", "caution", "cover", "restrict", "restrict_county", "exclude", "exclude_county"];

const WORDS: Record<UsState, StateWords<typeof CHECKIN_UT>> = perStateTable("CHECKIN", CHECKIN_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

export type CheckinActionLines = Record<CheckinAction, { foodWork: boolean; worker: string; personInCharge: string; citation: string }>;

/** The action table for a state (only the exclude_county citation differs; Utah's is CHECKIN_ACTIONS). */
export function checkinActionsFor(state?: UsState, county?: string | null): CheckinActionLines {
  const w = words(state);
  // Rule shapes: where the illness-reporting shape names hours (Nevada: NAC 446.056(1)(c), 24 hours), the person in charge's
  // line for a diagnosis or jaundice says who to tell and how fast. Utah's shape names none: its lines are unchanged.
  const ill = illnessLine(shapesAt({ state: state ?? "UT", county }));
  const tell = ill.withinHours !== null ? ` ${ill.line} (${ill.citation})` : "";
  const t: CheckinActionLines = {
  ok: { foodWork: true, worker: "You're good to work. Thanks.", personInCharge: "Ready.", citation: "Food Code 2-201.11" },
  caution: { foodWork: true, worker: "You can work. To be safe, don't handle ready-to-eat food or drinks today, and wash your hands often.", personInCharge: "Can work; keep off ready-to-eat food and drinks today (safer choice).", citation: "Food Code 2-201.12(J); source protocols: the safer choice when exposed but well" },
  cover: { foodWork: false, worker: "Cover it with a bandage and wear a glove on that hand, then you're good to work.", personInCharge: "Needs a bandage and glove before food work.", citation: "Food Code 2-401.13; 2-201.11(A)(1)(e)" },
  restrict: { foodWork: false, worker: "You can work today, but not with food, drinks, clean dishes or utensils. Ask your manager for cleaning or other tasks. Thanks for telling us.", personInCharge: "Restrict: no food, drinks, clean equipment or utensils today (cleaning, trash, paperwork only).", citation: "Food Code 2-201.12" },
  // A diagnosis with no symptoms (norovirus, Shigella or STEC where no highly susceptible population is served;
  // nontyphoidal Salmonella anywhere): restrict, 2-201.12(D)(2), (E)(2), (F)(2), (G). Back to food work only with the
  // regulatory authority's approval, 2-201.13(D)-(G). Not "today" only: tomorrow's check-in asks again, and the person
  // in charge holds it with a decision (returntowork.ts).
  restrict_county: { foodWork: false, worker: "You can work, but not with food, drinks, clean dishes or utensils. Ask your manager for cleaning or other tasks. The health department must say OK before you do food work again. Thanks for telling us.", personInCharge: "Restrict: no food, drinks, clean equipment or utensils (cleaning, trash, paperwork only). Back to food work only when the health department says OK.", citation: "Food Code 2-201.12(D)-(G), 2-201.13(D)-(G)" },
  exclude: { foodWork: false, worker: "Please don't work today. Rest and get well. Thank you for telling us.", personInCharge: "Exclude: not working today.", citation: "Food Code 2-201.12" },
  exclude_county: { foodWork: false, worker: "Please don't work, and see a doctor. Call your local health department before you come back. Thank you for telling us.", personInCharge: "Exclude. Return needs county health department approval; call them for guidance.", citation: cite("Food Code 2-201.12, 2-201.13", w.countyReturnCite) },
  };
  return tell ? { ...t, exclude_county: { ...t.exclude_county, personInCharge: t.exclude_county.personInCharge + tell }, restrict_county: { ...t.restrict_county, personInCharge: t.restrict_county.personInCharge + tell } } : t;
}

/** Utah's lines (a citation names Utah). Apps read `checkinActionsFor(place's state)` or CHECKIN_WORKER. */
export const CHECKIN_ACTIONS: CheckinActionLines = checkinActionsFor("UT");
/**
 * State-neutral: whether food work is allowed, and the worker's own line, are the same in every state; only the person in
 * charge's line (Nevada adds its 24-hour report, NAC 446.056(1)(c)) and the citation differ. Proven for all states by
 * packages/core/test/one-truth.test.ts. For the worker's own screen; the person in charge's line is checkinActionsFor(state).
 */
export const CHECKIN_WORKER: Record<CheckinAction, { foodWork: boolean; worker: string }> = Object.fromEntries(
  Object.entries(CHECKIN_ACTIONS).map(([k, v]) => [k, { foodWork: v.foodWork, worker: v.worker }]),
) as Record<CheckinAction, { foodWork: boolean; worker: string }>;

export function checkinAction(health: DailyCheckin["health"]): CheckinAction {
  const found: CheckinAction[] = ["ok"];
  if (health.exposedAtHome) found.push("caution");
  if (health.uncoveredSore) found.push(health.soreCovered ? "ok" : "cover");
  if (health.soreThroatFever) found.push("restrict");
  if (health.vomitDiarrhea) found.push("exclude");
  if (health.diagnosedOrExposed) found.push(diagnosedAction(health));
  if (health.jaundice) found.push("exclude_county");
  return found.reduce((a, b) => (SEVERITY.indexOf(b) > SEVERITY.indexOf(a) ? b : a));
}

/**
 * A doctor's diagnosis (see DIAGNOSED_FOLLOW_UPS for the Food Code rows). Every one of the six needs the regulatory
 * authority's approval to come back (2-201.13(B)-(G): "if the PERSON IN CHARGE obtains APPROVAL from the REGULATORY
 * AUTHORITY"), so the action is always a *_county one. An answer that isn't there (an older phone sent only the one
 * question) counts as the stricter one: exclude until the health department says OK, as before the split.
 */
function diagnosedAction(h: Health): CheckinAction {
  if (symptomKnown(h) || h.diagnosedSymptoms !== false) return "exclude_county"; // 2-201.12(A)(2), (B), (C)
  if (h.hsp) return h.diagnosedSalmonella === true ? "restrict_county" : "exclude_county"; // (G) | (B)(3), (C), (D)(1), (E)(1), (F)(1)
  return h.diagnosedHepATyphoid === false ? "restrict_county" : "exclude_county"; // (D)(2), (E)(2), (F)(2), (G) | (B)(3), (C)
}

/** The most severe action a "yes" to each question can lead to (checkinAction above). The follow-ups only lower it. */
const MAX_IF_YES: Partial<Record<keyof DailyCheckin["health"], CheckinAction>> = {
  vomitDiarrhea: "exclude", jaundice: "exclude_county", soreThroatFever: "restrict", diagnosedOrExposed: "exclude_county", uncoveredSore: "cover", exposedAtHome: "caution",
};

/**
 * The action from the answers so far; a question not answered yet counts as no, and a pending follow-up as its milder
 * answer (a sore as covered, a diagnosis as symptom-free and restrict-only), so the flow asks it rather than stopping.
 */
export function actionFromAnswers(a: Partial<DailyCheckin["health"]>): CheckinAction {
  const dx = a.diagnosedOrExposed === true;
  return checkinAction({
    vomitDiarrhea: false, jaundice: false, soreThroatFever: false, diagnosedOrExposed: false, uncoveredSore: false, ...a,
    ...(a.uncoveredSore && a.soreCovered === undefined ? { soreCovered: true } : {}),
    ...(dx && a.diagnosedSymptoms === undefined ? { diagnosedSymptoms: false } : {}),
    ...(dx && a.diagnosedHepATyphoid === undefined ? { diagnosedHepATyphoid: false } : {}),
    ...(dx && a.diagnosedSalmonella === undefined ? { diagnosedSalmonella: true } : {}),
  });
}

/**
 * Can this question still change what the person must do? Once an answer stops food work, ask only the questions whose
 * "yes" would make the action more severe (a diagnosis or jaundice turns "stay home" into "the health department must say
 * OK", Food Code 2-201.13), never the ones that can't change it (fix S5, 2026-09-29: the phone's early stop skipped both).
 * While they can still work, every question counts. The sore follow-up counts while it could lift "cover".
 */
export function canChangeAction(key: keyof DailyCheckin["health"], a: Partial<DailyCheckin["health"]>): boolean {
  if (typeof a[key] === "boolean") return false;
  if (key === "hsp") return false; // the location's, never asked
  // A diagnosis follow-up that applies always can: "yes, symptoms" is exclude, "no" at most restrict (see diagnosedAction).
  if (key in DIAGNOSED_FOLLOW_UPS) return diagnosedFollowUps(a).includes(key as FollowUpKey);
  const now = actionFromAnswers(a);
  if (key === "soreCovered") return a.uncoveredSore === true && SEVERITY.indexOf(now) <= SEVERITY.indexOf("cover");
  if (CHECKIN_ACTIONS[now].foodWork) return true;
  const most = MAX_IF_YES[key];
  return !!most && SEVERITY.indexOf(most) > SEVERITY.indexOf(now);
}

/** Stops food work and tells the person in charge (restrict, restrict_county, exclude, exclude_county); not cover. */
export const stopsFoodWork = (a: CheckinAction) => !CHECKIN_ACTIONS[a].foodWork && a !== "cover";

export function checkinOk(health: DailyCheckin["health"], handsWashed: boolean): boolean {
  return handsWashed && CHECKIN_ACTIONS[checkinAction(health)].foodWork;
}

/**
 * Handwashing reminders: at the start, hourly during service, and right after any raw-meat temperature is logged.
 * One pair of hours; for a location use hours.ts handwashTimesOn (weekly hours, closed days, overnight).
 */
export function handwashNudges(openAt: string, closeAt: string): string[] {
  const toMin = (s: string) => { const [h, m] = s.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
  const out: string[] = [];
  for (let m = toMin(openAt) + 60; m < toMin(closeAt); m += 60) out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
  return out;
}
export const HANDWASH_AFTER_STAGES = ["cook poultry", "cook ground", "cook whole/fish"];
